import { GET_AD_ACCOUNT_STATUS } from 'client/js/main/actions/SANPartnerActions';
import { deepCopy } from '../../../../../shared/utils';
import { APPLE_SAVE_CONFIG } from '../../../main/actions/AppleSearchAdsActions';
import { FB_SAVE_CONFIG } from '../../../main/actions/FacebookActions';
import { GET_GOOGLE_USER_INFO, GOOGLE_ADWORDS_SAVE, REVOKE_GOOGLE_AUTH } from '../../../main/actions/GoogleActions';
import type { AdPartner, AdPartnerState } from '../../../main/types/adPartnerTypes';
import { APP_CHANGE_ACTION_TYPE } from '../../constants/ReduxAsyncActions.constants';
import { AdPartnerService } from '../../services/AdPartner.service';
import { ReduxAsyncActionsService } from '../../services/ReduxAsyncActions.service';
import type { WrappedReducer } from '../../types/ReduxAsyncActions.types';
import { INIT_STATE } from './AdPartner.constants';
import type { AdPartnerAction } from './actions';
import {
  CHANGE_READY_FOR_SAVE,
  CREATE_INTERNAL_AD_PARTNER,
  DELETE_INTERNAL_PARTNER,
  DISABLE,
  GET_AD_ONTOLOGY,
  GET_CUSTOM_PARTNERS_LIMIT,
  GET_PARTNERS,
  GET_PARTNER_FOR_ORG,
  RESET_SETTINGS,
  ROLLBACK_UNSAVED_CHANGES,
  SAVE_ENABLE,
  SAVE_POSTBACK,
  SELECT,
  UPDATE,
} from './actions';
import { GET_AD_PARTNER_CONNECTIONS } from './actions/getAdPartnerConnections.action';
import { GET_CONNECTION_DATA } from './actions/getConnectionData.action';
import { UPDATE_CONNECTION } from './actions/updateConnection.action';

export const replacePartnerInPartnersList = (partners: AdPartner[], partner: AdPartner) => {
  const { machine_name } = partner;
  const index = partners.findIndex((statePartner) => statePartner.machine_name === machine_name);

  if (index < 0) {
    return partners;
  }

  return [...partners.slice(0, index), partner, ...partners.slice(index + 1)];
};

const handler = (state: WrappedReducer<AdPartnerState>, action: AdPartnerAction): WrappedReducer<AdPartnerState> => {
  let partners: AdPartner[] | undefined;
  let partner: AdPartner | undefined;
  let enabledPartner: AdPartner | undefined;
  let machine_name: string | undefined;
  let index: number | undefined;

  switch (action.type) {
    case GET_PARTNERS.response:
      partners = AdPartnerService.sortByCompanyName(action.data.partners);
      const selected = typeof action.data.selected === 'string' ? action.data.selected : action.data.selected?.machine_name;

      const selectedPartner = selected
        ? partners.find((statePartner) => statePartner.machine_name === selected)
        : state.selectedPartner;

      return {
        ...state,
        partners,
        selectedPartner,
        loading: false,
        hasPartnersLoaded: true,
        partnersBackup: deepCopy(partners),
      };

    case CREATE_INTERNAL_AD_PARTNER.response:
      const internalPartner = action.data.selectedPartner;
      const partnersWithNewInternalPartner = [...state.partners, internalPartner];
      partners = AdPartnerService.sortByCompanyName(partnersWithNewInternalPartner);

      return {
        ...state,
        partners,
        selectedPartner: internalPartner,
        loading: false,
        hasPartnersLoaded: true,
        partnersBackup: deepCopy(partners),
      };

    case GET_PARTNER_FOR_ORG.response:
      const partnerData = action.data;
      const orgPartners = { ...state.orgPartners, [partnerData.machine_name]: partnerData };

      return {
        ...state,
        orgPartners,
        loading: false,
        hasPartnersLoaded: true,
      };

    case GET_AD_ONTOLOGY.response:
      return {
        ...state,
        ad_ontology: action.data,
      };

    case REVOKE_GOOGLE_AUTH.response:
    case FB_SAVE_CONFIG.response:
    case GOOGLE_ADWORDS_SAVE.response:
    case APPLE_SAVE_CONFIG.response:

    case SAVE_ENABLE.response:
      machine_name = action.data.machine_name;
      partners = deepCopy(state.partners);

      index = partners.findIndex((partnerInArray) => partnerInArray.machine_name === machine_name);

      enabledPartner = { ...partners[index], ...action.data };

      partners = [...partners.slice(0, index), enabledPartner, ...partners.slice(index + 1)];

      return {
        ...state,
        partners,
        selectedPartner: enabledPartner,
        unsavedChanges: false,
        loading: false,
        mfReadyForSave: false,
        partnersBackup: deepCopy(partners),
      };

    case DISABLE.response:
      machine_name = action.data.machine_name;
      index = state.partners.findIndex((statePartner) => statePartner.machine_name === machine_name);
      const updatedPartner = deepCopy(state.partners[index]);
      updatedPartner.enabled = false;
      partners = [...state.partners.slice(0, index), updatedPartner, ...state.partners.slice(index + 1)];

      return {
        ...state,
        partners,
        selectedPartner: null,
        unsavedChanges: false,
        loading: false,
        partnersBackup: deepCopy(partners),
      };

    case GET_AD_ACCOUNT_STATUS.response:
      return state;

    case SAVE_POSTBACK.response:
      /* service responds with entire updated partner object
       * we only want to update the new postback so we don't
       * overwrite any unsaved changes
       */
      partner = action.data.partner;
      const { event } = action.data;
      machine_name = partner.machine_name;
      const { postbacks } = partner;
      // As we now allow multiple postbacks for the same event (INTENG-5855),
      // we are going to assume that the last postback in the list for that
      // event is the new one.
      const postbacksWithSameEvent = postbacks.filter((postback) => postback.event === event);
      const newPostback = postbacksWithSameEvent[postbacksWithSameEvent.length - 1];
      index = state.partners.findIndex((statePartner) => statePartner.machine_name === machine_name);
      const partnerFromList = deepCopy(state.partners[index]);

      /**
       * This is a weird case after migration where ad partner entries were
       * migrated without postbacks. On the other hand, blueprint for
       * these ad partners have postbacks which is an object.
       * If an active ad partner doesn't have postbacks, we copy over
       * from blueprint as a object
       */
      partnerFromList.postbacks =
        partnerFromList.postbacks && Array.isArray(partnerFromList.postbacks)
          ? partnerFromList.postbacks.concat(newPostback)
          : [newPostback];
      partners = [...state.partners.slice(0, index), partnerFromList, ...state.partners.slice(index + 1)];

      return {
        ...state,
        partners,
        selectedPartner: partnerFromList,
        loading: false,
        partnersBackup: deepCopy(partners),
      };

    case RESET_SETTINGS.response:
      partner = action.data.partnerBlueprint as unknown as AdPartner;
      const enabledPartners = action.data.enabledPartners;
      const selectedPartnerEnabled = Boolean(enabledPartners[partner.machine_name]);

      // Check for org/app-level enablement when the ad network entries are reset
      if (!selectedPartnerEnabled) {
        partner.neverConfigured = true;
        partners = replacePartnerInPartnersList(state.partners, partner);

        return {
          ...state,
          unsavedChanges: false,
          loading: false,
          selectedPartner: partner,
          partnersBackup: deepCopy(partners),
        };
      }

      return {
        ...state,
        unsavedChanges: false,
        loading: false,
      };

    case DELETE_INTERNAL_PARTNER.response:
      const updatedPartners = state.partners.filter((statePartner) => {
        return statePartner.machine_name !== action.data.partner.machine_name;
      });

      return {
        ...state,
        partners: updatedPartners,
        unsavedChanges: false,
        loading: false,
        selectedPartner: null,
        partnersBackup: deepCopy(updatedPartners),
      };

    case SELECT:
      return {
        ...state,
        selectedPartner: action.data,
      };

    case UPDATE:
      const { mfReadyForSave = false, ...config } = action.data;
      partners = replacePartnerInPartnersList(state.partners, config);

      return {
        ...state,
        partners,
        selectedPartner: config,
        unsavedChanges: true,
        mfReadyForSave,
      };

    case ROLLBACK_UNSAVED_CHANGES:
      const selectedPartnerBackup = state.partnersBackup.find(
        (partnerBackup) => partnerBackup.machine_name === state.selectedPartner?.machine_name
      );
      return {
        ...state,
        partners: deepCopy(state.partnersBackup),
        selectedPartner: selectedPartnerBackup,
        unsavedChanges: false,
        error: null,
      };

    case GET_GOOGLE_USER_INFO.response:
      partners = deepCopy(state.partners);
      index = partners.findIndex((statePartner) => statePartner.machine_name === 'a_google_adwords');

      const credentialsWithUserInfo = { ...partners[index].credentials, ...action.data };

      enabledPartner = { ...partners[index], credentials: credentialsWithUserInfo };

      partners = [...partners.slice(0, index), enabledPartner, ...partners.slice(index + 1)];

      return {
        ...state,
        partners,
        selectedPartner: enabledPartner,
        loading: false,
        partnersBackup: deepCopy(partners),
      };

    case CHANGE_READY_FOR_SAVE:
      return { ...state, mfReadyForSave: action.data };

    case GET_CUSTOM_PARTNERS_LIMIT.request:
      return {
        ...state,
        customPartnerLimitsError: null,
        customPartnerLimitsLoading: true,
      };

    case GET_CUSTOM_PARTNERS_LIMIT.response:
      return {
        ...state,
        customPartnerLimits: action.data,
        customPartnerLimitsLoading: false,
      };

    case GET_CUSTOM_PARTNERS_LIMIT.error:
      return {
        ...state,
        customPartnerLimitsError: action.data,
        customPartnerLimitsLoading: false,
      };

    case GET_CONNECTION_DATA.request:
      return {
        ...state,
        connectionsLoading: true,
      };

    case GET_CONNECTION_DATA.response:
      const connections = state.connections || [];
      return {
        ...state,
        connections: connections.find((connection) => connection.credential_id === action.data.credential_id)
          ? connections.map((connection) =>
              connection.credential_id === action.data.credential_id ? action.data : connection
            )
          : [action.data],
        connectionsLoading: false,
      };

    case GET_CONNECTION_DATA.error:
      return {
        ...state,
        connectionsError: action.data,
        connectionsLoading: false,
      };

    case GET_AD_PARTNER_CONNECTIONS.request:
      return {
        ...state,
        connectionsLoading: true,
      };

    case GET_AD_PARTNER_CONNECTIONS.response:
      return {
        ...state,
        connections: action.data,
        connectionsLoading: false,
      };

    case GET_AD_PARTNER_CONNECTIONS.error:
      return {
        ...state,
        connectionsError: action.data,
        connectionsLoading: false,
      };

    case UPDATE_CONNECTION.request:
      return {
        ...state,
        connectionsLoading: true,
      };

    case UPDATE_CONNECTION.response:
      const stateConnections = state.connections || [];

      return {
        ...state,
        connections: stateConnections.map((connection) => {
          if (connection.credential_id === action.data.credential_id) {
            return {
              ...connection,
              ...action.data,
            };
          } else {
            return connection;
          }
        }),
        connectionsLoading: false,
      };

    default:
      return state;
  }
};

const AdPartnerReducer = ReduxAsyncActionsService.wrapReducer(INIT_STATE, handler, {
  request: [
    GET_PARTNERS,
    GET_PARTNER_FOR_ORG,
    SAVE_ENABLE,
    DISABLE,
    SAVE_POSTBACK,
    RESET_SETTINGS,
    GET_GOOGLE_USER_INFO,
    GOOGLE_ADWORDS_SAVE,
    REVOKE_GOOGLE_AUTH,
    FB_SAVE_CONFIG,
    APPLE_SAVE_CONFIG,
    CREATE_INTERNAL_AD_PARTNER,
  ],
  error: [
    GET_PARTNERS,
    GET_PARTNER_FOR_ORG,
    SAVE_ENABLE,
    DISABLE,
    SAVE_POSTBACK,
    RESET_SETTINGS,
    GET_GOOGLE_USER_INFO,
    GOOGLE_ADWORDS_SAVE,
    REVOKE_GOOGLE_AUTH,
    FB_SAVE_CONFIG,
    APPLE_SAVE_CONFIG,
    CREATE_INTERNAL_AD_PARTNER,
  ],
  resetOn: [APP_CHANGE_ACTION_TYPE],
});

export default AdPartnerReducer;
